import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// ============ TYPE DEFINITIONS ============

export type CapTableStage =
    | 'pre-incorporation'
    | 'just-incorporated'
    | 'pre-seed'
    | 'seed'
    | 'series-a'
    | 'series-b-plus'
    | 'pre-ipo';

export interface CompanyInfo {
    companyName: string;
    cin: string;
    stateOfIncorporation: string;
    dateOfIncorporation: string;
    registeredAddress: string;
    authorizedCapital: string;
    authorizedShares: string;
    parValue: string;
    currency: string;  // Default: 'INR'
}

export interface BasicsInfo {
    companyName: string;
    plannedState: string;
    plannedIncorporationDate: string;
    plannedAuthorizedShares: string;
    plannedParValue: string;
}

export interface Founder {
    id: string;
    name: string;
    email: string;
    shares: string;
    shareClass: string;
    vestingSchedule: string;
    vestingCliff: string;
    vestingPeriod: string;
    accelerationClause: string;
    dateJoined: string;
    // Designation fields
    designation?: string;
    customDesignation?: string;
    // Custom vesting fields (when vestingSchedule === 'custom')
    customVestingMonths?: number;
    customCliffMonths?: number;
    customVestingFrequency?: 'monthly' | 'quarterly' | 'annually';
}

export interface Investor {
    id: string;
    name: string;
    type: 'individual' | 'angel' | 'vc' | 'institution' | 'corporate';
    amountInvested: string;
    instrumentType: 'equity' | 'safe' | 'convertible-note' | 'ccd';
    shareClass: string;
    shares: string;
    pricePerShare: string;
    investmentDate: string;
    boardSeat: boolean;
    proRataRights: boolean;
    currency?: string;  // Currency for investment amount
}

export interface Convertible {
    id: string;
    investorName: string;
    instrumentType: 'safe' | 'convertible-note' | 'ccd';
    principalAmount: string;
    valuationCap: string;
    discountRate: string;
    interestRate: string;
    maturityDate: string;
    conversionTrigger: string;
    mfnClause: boolean;
    proRataRights: boolean;
    status: 'outstanding' | 'converted' | 'cancelled';
    currency?: string;  // Currency for amounts
}

export interface FundingRound {
    id: string;
    roundName: string;
    preMoneyValuation: string;
    amountRaised: string;
    pricePerShare: string;
    shareClass: string;
    securityType: string;
    liquidationPreference: string;
    participatingPreferred: boolean;
    antiDilutionProtection: 'none' | 'broad-based' | 'narrow-based' | 'full-ratchet';
    leadInvestor: string;
    closingDate: string;
    currency?: string;  // Currency for valuation and amounts
}

export interface ESOPPool {
    poolSize: string;
    poolPercentage: string;
    vestingSchedule: string;
    defaultCliff: string;
    defaultVestingPeriod: string;
}

export interface ESOPPlanning {
    plannedPoolPercentage: string;
    vestingSchedule: string;
}

export interface ShareClass {
    id: string;
    className: string;
    type: 'common' | 'preferred';
    votingRights: string;
    liquidationPreference: string;
    conversionRatio: string;
    dividendRights: string;
    antiDilutionRights: string;
    authorizedShares: string;
    issuedShares: string;
}

export interface Warrant {
    id: string;
    holderName: string;
    numberOfWarrants: string;
    exercisePrice: string;
    expirationDate: string;
    underlyingShareClass: string;
    status: 'outstanding' | 'exercised' | 'expired';
}

export interface DebtInstrument {
    id: string;
    lenderName: string;
    principalAmount: string;
    interestRate: string;
    maturityDate: string;
    conversionOption: boolean;
    collateral: string;
    status: 'active' | 'repaid' | 'converted';
}

export interface SecondaryTransaction {
    id: string;
    sellerName: string;
    buyerName: string;
    numberOfShares: string;
    pricePerShare: string;
    shareClass: string;
    transactionDate: string;
    approvalStatus: 'pending' | 'approved' | 'completed';
}

// ============ STATE INTERFACE ============

export interface CapTableState {
    // Project metadata
    projectId: string | null;
    stage: CapTableStage | null;

    // Pre-incorporation basics
    basicsInfo: BasicsInfo;

    // Company Information (for incorporated companies)
    companyInfo: CompanyInfo;

    // Equity holders
    founders: Founder[];
    investors: Investor[];

    // Instruments
    convertibles: Convertible[];
    esopPool: ESOPPool;
    esopPlanning: ESOPPlanning;
    shareClasses: ShareClass[];
    warrants: Warrant[];
    debtInstruments: DebtInstrument[];

    // Funding history
    fundingRounds: FundingRound[];
    secondaryTransactions: SecondaryTransaction[];

    // UI State
    currentSectionIndex: number;
    applicableSections: string[];
    completedSections: string[];

    // Optional section flags (null = not yet decided, true/false = user choice)
    hasConvertibles: boolean | null;
    hasEsop: boolean | null;

    // Loading/Error states
    isLoading: boolean;
    isSaving: boolean;
    error: string | null;
}

// ============ INITIAL STATE ============

const initialState: CapTableState = {
    projectId: null,
    stage: null,

    basicsInfo: {
        companyName: '',
        plannedState: '',
        plannedIncorporationDate: '',
        plannedAuthorizedShares: '',
        plannedParValue: '',
    },

    companyInfo: {
        companyName: '',
        cin: '',
        stateOfIncorporation: '',
        dateOfIncorporation: '',
        registeredAddress: '',
        authorizedCapital: '',
        authorizedShares: '',
        parValue: '',
        currency: 'INR',
    },

    founders: [],
    investors: [],
    convertibles: [],
    esopPool: {
        poolSize: '',
        poolPercentage: '',
        vestingSchedule: '4-year-1-cliff',
        defaultCliff: '12 months',
        defaultVestingPeriod: '48 months',
    },
    esopPlanning: {
        plannedPoolPercentage: '',
        vestingSchedule: '4-year-1-cliff',
    },
    shareClasses: [],
    warrants: [],
    debtInstruments: [],
    fundingRounds: [],
    secondaryTransactions: [],

    currentSectionIndex: 0,
    applicableSections: [],
    completedSections: [],

    hasConvertibles: null,
    hasEsop: null,

    isLoading: false,
    isSaving: false,
    error: null,
};

// ============ STAGE CONFIGURATION ============

export const STAGE_CONFIG: Record<CapTableStage, { sections: string[]; name: string; description: string }> = {
    'pre-incorporation': {
        name: 'Pre-Incorporation',
        description: 'Planning founder equity split before incorporating',
        sections: ['basics', 'founders', 'esop_planning'],
    },
    'just-incorporated': {
        name: 'Just Incorporated',
        description: 'Document founder ownership after incorporation',
        sections: ['company_info', 'founders', 'esop'],
    },
    'pre-seed': {
        name: 'Pre-Seed',
        description: 'Track early investors and convertible instruments',
        sections: ['company_info', 'founders', 'angels', 'convertibles', 'esop'],
    },
    'seed': {
        name: 'Seed',
        description: 'Full cap table with priced round',
        sections: ['company_info', 'founders', 'investors', 'funding_round', 'esop'],
    },
    'series-a': {
        name: 'Series A',
        description: 'Multiple rounds with complex terms',
        sections: ['company_info', 'share_classes', 'founders', 'investors', 'funding_round', 'convertibles', 'esop'],
    },
    'series-b-plus': {
        name: 'Series B+',
        description: 'Multiple VC rounds, debt, and warrants',
        sections: ['company_info', 'share_classes', 'founders', 'investors', 'funding_round', 'convertibles', 'esop', 'warrants', 'debt', 'secondary'],
    },
    'pre-ipo': {
        name: 'Pre-IPO',
        description: 'Audit-ready documentation',
        sections: ['company_info', 'share_classes', 'founders', 'investors', 'funding_round', 'convertibles', 'esop', 'warrants', 'debt', 'secondary'],
    },
};

// ============ SLICE ============

const capTableSlice = createSlice({
    name: 'capTable',
    initialState,
    reducers: {
        // Stage and project management
        setStage: (state, action: PayloadAction<CapTableStage>) => {
            state.stage = action.payload;
            const config = STAGE_CONFIG[action.payload];
            state.applicableSections = config.sections;
            state.currentSectionIndex = 0;
            state.completedSections = [];
        },

        setProjectId: (state, action: PayloadAction<string | null>) => {
            state.projectId = action.payload;
        },

        // Navigation
        goToNextSection: (state) => {
            const currentSection = state.applicableSections[state.currentSectionIndex];
            if (currentSection && !state.completedSections.includes(currentSection)) {
                state.completedSections.push(currentSection);
            }
            if (state.currentSectionIndex < state.applicableSections.length - 1) {
                state.currentSectionIndex += 1;
            }
        },

        goToPreviousSection: (state) => {
            if (state.currentSectionIndex > 0) {
                state.currentSectionIndex -= 1;
            }
        },

        goToSection: (state, action: PayloadAction<number>) => {
            if (action.payload >= 0 && action.payload < state.applicableSections.length) {
                state.currentSectionIndex = action.payload;
            }
        },

        markSectionComplete: (state, action: PayloadAction<string>) => {
            if (!state.completedSections.includes(action.payload)) {
                state.completedSections.push(action.payload);
            }
        },

        // Basics info (pre-incorporation)
        setBasicsInfo: (state, action: PayloadAction<Partial<BasicsInfo>>) => {
            state.basicsInfo = { ...state.basicsInfo, ...action.payload };
        },

        // Company info
        setCompanyInfo: (state, action: PayloadAction<Partial<CompanyInfo>>) => {
            state.companyInfo = { ...state.companyInfo, ...action.payload };
        },

        // Founders CRUD
        addFounder: (state, action: PayloadAction<Founder>) => {
            state.founders.push(action.payload);
        },
        updateFounder: (state, action: PayloadAction<{ id: string; data: Partial<Founder> }>) => {
            const index = state.founders.findIndex(f => f.id === action.payload.id);
            if (index !== -1) {
                state.founders[index] = { ...state.founders[index], ...action.payload.data };
            }
        },
        removeFounder: (state, action: PayloadAction<string>) => {
            state.founders = state.founders.filter(f => f.id !== action.payload);
        },
        setFounders: (state, action: PayloadAction<Founder[]>) => {
            state.founders = action.payload;
        },

        // Investors CRUD
        addInvestor: (state, action: PayloadAction<Investor>) => {
            state.investors.push(action.payload);
        },
        updateInvestor: (state, action: PayloadAction<{ id: string; data: Partial<Investor> }>) => {
            const index = state.investors.findIndex(i => i.id === action.payload.id);
            if (index !== -1) {
                state.investors[index] = { ...state.investors[index], ...action.payload.data };
            }
        },
        removeInvestor: (state, action: PayloadAction<string>) => {
            state.investors = state.investors.filter(i => i.id !== action.payload);
        },
        setInvestors: (state, action: PayloadAction<Investor[]>) => {
            state.investors = action.payload;
        },

        // Convertibles CRUD
        addConvertible: (state, action: PayloadAction<Convertible>) => {
            state.convertibles.push(action.payload);
        },
        updateConvertible: (state, action: PayloadAction<{ id: string; data: Partial<Convertible> }>) => {
            const index = state.convertibles.findIndex(c => c.id === action.payload.id);
            if (index !== -1) {
                state.convertibles[index] = { ...state.convertibles[index], ...action.payload.data };
            }
        },
        removeConvertible: (state, action: PayloadAction<string>) => {
            state.convertibles = state.convertibles.filter(c => c.id !== action.payload);
        },
        setConvertibles: (state, action: PayloadAction<Convertible[]>) => {
            state.convertibles = action.payload;
        },

        // ESOP
        setESOPPool: (state, action: PayloadAction<Partial<ESOPPool>>) => {
            state.esopPool = { ...state.esopPool, ...action.payload };
        },

        setESOPPlanning: (state, action: PayloadAction<Partial<ESOPPlanning>>) => {
            state.esopPlanning = { ...state.esopPlanning, ...action.payload };
        },

        // Optional section flags
        setHasConvertibles: (state, action: PayloadAction<boolean | null>) => {
            state.hasConvertibles = action.payload;
        },

        setHasEsop: (state, action: PayloadAction<boolean | null>) => {
            state.hasEsop = action.payload;
        },

        // Share Classes
        addShareClass: (state, action: PayloadAction<ShareClass>) => {
            state.shareClasses.push(action.payload);
        },
        updateShareClass: (state, action: PayloadAction<{ id: string; data: Partial<ShareClass> }>) => {
            const index = state.shareClasses.findIndex(sc => sc.id === action.payload.id);
            if (index !== -1) {
                state.shareClasses[index] = { ...state.shareClasses[index], ...action.payload.data };
            }
        },
        removeShareClass: (state, action: PayloadAction<string>) => {
            state.shareClasses = state.shareClasses.filter(sc => sc.id !== action.payload);
        },
        setShareClasses: (state, action: PayloadAction<ShareClass[]>) => {
            state.shareClasses = action.payload;
        },

        // Funding Rounds
        addFundingRound: (state, action: PayloadAction<FundingRound>) => {
            state.fundingRounds.push(action.payload);
        },
        updateFundingRound: (state, action: PayloadAction<{ id: string; data: Partial<FundingRound> }>) => {
            const index = state.fundingRounds.findIndex(fr => fr.id === action.payload.id);
            if (index !== -1) {
                state.fundingRounds[index] = { ...state.fundingRounds[index], ...action.payload.data };
            }
        },
        removeFundingRound: (state, action: PayloadAction<string>) => {
            state.fundingRounds = state.fundingRounds.filter(fr => fr.id !== action.payload);
        },
        setFundingRounds: (state, action: PayloadAction<FundingRound[]>) => {
            state.fundingRounds = action.payload;
        },

        // Warrants
        addWarrant: (state, action: PayloadAction<Warrant>) => {
            state.warrants.push(action.payload);
        },
        updateWarrant: (state, action: PayloadAction<{ id: string; data: Partial<Warrant> }>) => {
            const index = state.warrants.findIndex(w => w.id === action.payload.id);
            if (index !== -1) {
                state.warrants[index] = { ...state.warrants[index], ...action.payload.data };
            }
        },
        removeWarrant: (state, action: PayloadAction<string>) => {
            state.warrants = state.warrants.filter(w => w.id !== action.payload);
        },
        setWarrants: (state, action: PayloadAction<Warrant[]>) => {
            state.warrants = action.payload;
        },

        // Debt Instruments
        addDebtInstrument: (state, action: PayloadAction<DebtInstrument>) => {
            state.debtInstruments.push(action.payload);
        },
        updateDebtInstrument: (state, action: PayloadAction<{ id: string; data: Partial<DebtInstrument> }>) => {
            const index = state.debtInstruments.findIndex(d => d.id === action.payload.id);
            if (index !== -1) {
                state.debtInstruments[index] = { ...state.debtInstruments[index], ...action.payload.data };
            }
        },
        removeDebtInstrument: (state, action: PayloadAction<string>) => {
            state.debtInstruments = state.debtInstruments.filter(d => d.id !== action.payload);
        },
        setDebtInstruments: (state, action: PayloadAction<DebtInstrument[]>) => {
            state.debtInstruments = action.payload;
        },

        // Secondary Transactions
        addSecondaryTransaction: (state, action: PayloadAction<SecondaryTransaction>) => {
            state.secondaryTransactions.push(action.payload);
        },
        updateSecondaryTransaction: (state, action: PayloadAction<{ id: string; data: Partial<SecondaryTransaction> }>) => {
            const index = state.secondaryTransactions.findIndex(st => st.id === action.payload.id);
            if (index !== -1) {
                state.secondaryTransactions[index] = { ...state.secondaryTransactions[index], ...action.payload.data };
            }
        },
        removeSecondaryTransaction: (state, action: PayloadAction<string>) => {
            state.secondaryTransactions = state.secondaryTransactions.filter(st => st.id !== action.payload);
        },
        setSecondaryTransactions: (state, action: PayloadAction<SecondaryTransaction[]>) => {
            state.secondaryTransactions = action.payload;
        },

        // Loading states
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setSaving: (state, action: PayloadAction<boolean>) => {
            state.isSaving = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },

        // Load full state (for editing existing project)
        loadCapTable: (state, action: PayloadAction<Partial<CapTableState>>) => {
            return { ...state, ...action.payload };
        },

        // Load cap table data from database (for resuming a project)
        loadCapTableData: (state, action: PayloadAction<{
            companyInfo?: Partial<CompanyInfo>;
            basicsInfo?: Partial<BasicsInfo>;
            founders?: Founder[];
            investors?: Investor[];
            convertibles?: Convertible[];
            esopPool?: Partial<ESOPPool>;
            fundingRounds?: FundingRound[];
            shareClasses?: ShareClass[];
            hasConvertibles?: boolean | null;
            hasEsop?: boolean | null;
            currentSectionIndex?: number;
            completedSections?: string[];
        }>) => {
            const data = action.payload;

            if (data.companyInfo) {
                state.companyInfo = { ...state.companyInfo, ...data.companyInfo };
            }
            if (data.basicsInfo) {
                state.basicsInfo = { ...state.basicsInfo, ...data.basicsInfo };
            }
            if (data.founders) {
                state.founders = data.founders;
            }
            if (data.investors) {
                state.investors = data.investors;
            }
            if (data.convertibles) {
                state.convertibles = data.convertibles;
            }
            if (data.esopPool) {
                state.esopPool = { ...state.esopPool, ...data.esopPool };
            }
            if (data.fundingRounds) {
                state.fundingRounds = data.fundingRounds;
            }
            if (data.shareClasses) {
                state.shareClasses = data.shareClasses;
            }
            if (data.hasConvertibles !== undefined) {
                state.hasConvertibles = data.hasConvertibles;
            }
            if (data.hasEsop !== undefined) {
                state.hasEsop = data.hasEsop;
            }
            if (data.currentSectionIndex !== undefined) {
                state.currentSectionIndex = data.currentSectionIndex;
            }
            if (data.completedSections) {
                state.completedSections = data.completedSections;
            }
        },

        // Reset
        resetCapTable: () => initialState,
    },
});

export const {
    setStage,
    setProjectId,
    goToNextSection,
    goToPreviousSection,
    goToSection,
    markSectionComplete,
    setBasicsInfo,
    setCompanyInfo,
    addFounder,
    updateFounder,
    removeFounder,
    setFounders,
    addInvestor,
    updateInvestor,
    removeInvestor,
    setInvestors,
    addConvertible,
    updateConvertible,
    removeConvertible,
    setConvertibles,
    setESOPPool,
    setESOPPlanning,
    setHasConvertibles,
    setHasEsop,
    addShareClass,
    updateShareClass,
    removeShareClass,
    setShareClasses,
    addFundingRound,
    updateFundingRound,
    removeFundingRound,
    setFundingRounds,
    addWarrant,
    updateWarrant,
    removeWarrant,
    setWarrants,
    addDebtInstrument,
    updateDebtInstrument,
    removeDebtInstrument,
    setDebtInstruments,
    addSecondaryTransaction,
    updateSecondaryTransaction,
    removeSecondaryTransaction,
    setSecondaryTransactions,
    setLoading,
    setSaving,
    setError,
    loadCapTable,
    loadCapTableData,
    resetCapTable,
} = capTableSlice.actions;

export default capTableSlice.reducer;
